import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  // Subtle randomized, layered gradient background (same as Home)
  const backgroundStyle = useMemo<React.CSSProperties>(() => {
    const rand = (min: number, max: number) => Math.random() * (max - min) + min;

    const hue = rand(205, 220);
    const saturation = rand(65, 78);
    const lightNearWhite = rand(96, 98);
    const lightBlue = rand(88, 92);
    const alphaSoft1 = rand(0.06, 0.1);
    const alphaSoft2 = rand(0.04, 0.08);

    const centerX = rand(30, 70);
    const centerY = rand(15, 45);

    const verticalAngle = 180;
    const horizontalAngle = 90;

    const color1 = `hsla(${hue}, ${saturation}%, ${lightNearWhite}%, 1)`;
    const color2 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, 1)`;
    const tint1 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, ${alphaSoft1})`;
    const tint2 = `hsla(${hue}, ${saturation}%, ${lightNearWhite}%, ${alphaSoft2})`;

    const backgroundImage = [
      `radial-gradient(ellipse at ${centerX}% ${centerY}%, ${tint1} 0%, transparent 60%)`,
      `linear-gradient(${verticalAngle}deg, #ffffff 0%, ${color2} 100%)`,
      `linear-gradient(${horizontalAngle}deg, ${tint2} 0%, transparent 50%, ${tint2} 100%)`,
    ].join(', ');

    return {
      backgroundImage,
      backgroundColor: '#ffffff',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover, cover, cover',
      backgroundAttachment: 'scroll',
    };
  }, []);

  const lastUpdated = 'November 14, 2024';

  return (
    <>
      <Helmet>
        <title>Privacy Policy - Attain Domains</title>
        <meta
          name="description"
          content="Attain Domains Privacy Policy: How we collect, use, and protect your data. Learn about our data practices, your rights, and how to contact us."
        />
        <link rel="canonical" href="https://attaindomains.com/privacy" />
      </Helmet>

      <div className="min-h-screen py-16" style={backgroundStyle}>
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg">
              Privacy Policy
            </h1>
            <div className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed font-normal">
              Last updated: {lastUpdated}
            </div>
          </div>

          <div className="prose prose-lg max-w-none space-y-8">
            {/* 1. Introduction & Scope */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                1. Introduction & Scope
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  Welcome to Attain Domains. We are Attain Domains, operating AttainDomains.com, a
                  premium domain marketplace where you can buy and lease domains, including
                  Lease-to-Own plans.
                </p>
                <p>
                  This Privacy Policy explains how we collect, use, and share information when you
                  visit our website, browse domains, create an account, or purchase or lease a
                  domain. We are committed to protecting your privacy and being transparent about
                  our data practices.
                </p>
                <p>
                  <strong>Note:</strong> This Privacy Policy is provided for transparency and does
                  not constitute formal legal advice. Attain Domains may update this policy after
                  consulting legal counsel.
                </p>
              </div>
            </section>

            {/* 2. Information We Collect */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                2. Information We Collect
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">
                    Information You Provide to Us
                  </h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>
                      <strong>Account details:</strong> When you create an account, we collect
                      your name, email address, and password (stored securely and hashed).
                    </li>
                    <li>
                      <strong>Checkout information:</strong> When you purchase or lease a domain,
                      we collect your name, email address, and billing information. Payment card
                      numbers are processed securely by Stripe and are never stored on our servers.
                    </li>
                    <li>
                      <strong>Support messages:</strong> When you contact us via our contact form
                      or email, we collect the contents of your message, your name, email address,
                      and any other information you choose to provide.
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">
                    Information Collected Automatically
                  </h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>
                      <strong>Log data:</strong> We automatically collect IP addresses, browser
                      type, device information, pages visited, timestamps, and referring URLs.
                    </li>
                    <li>
                      <strong>Approximate location:</strong> We may derive approximate geographic
                      location from IP addresses for analytics and security purposes.
                    </li>
                    <li>
                      <strong>Device and usage information:</strong> Our analytics tools collect
                      information about how you interact with our website, including clicks,
                      scrolls, and time spent on pages.
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">
                    Information from Payment and Service Providers
                  </h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>
                      <strong>Stripe:</strong> We receive payment status, transaction metadata, and
                      customer identifiers from Stripe. We do not receive or store full payment card
                      numbers, CVV codes, or other sensitive payment authentication data.
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 3. How We Use Your Information */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                3. How We Use Your Information
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">
                    To Operate and Maintain the Site and Services
                  </h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Loading pages and delivering content</li>
                    <li>Hosting and database operations</li>
                    <li>Logging and error tracking for troubleshooting</li>
                    <li>Maintaining security and preventing fraud</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">
                    To Process Purchases and Lease-to-Own Plans
                  </h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Handling orders and processing payments through Stripe</li>
                    <li>Issuing invoices and receipts</li>
                    <li>Managing recurring payments for Lease-to-Own subscriptions</li>
                    <li>Facilitating domain transfers after full payment</li>
                    <li>Providing DNS access and domain management tools</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">
                    To Communicate with You
                  </h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Sending order confirmations, invoices, and receipts</li>
                    <li>Providing domain transfer instructions and updates</li>
                    <li>Responding to support requests and inquiries</li>
                    <li>Sending important service updates (e.g., account verification, security alerts)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">
                    To Improve and Secure the Service
                  </h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>
                      Using analytics to understand how the site is used and identify areas for
                      improvement
                    </li>
                    <li>Monitoring errors and performance issues via error tracking tools</li>
                    <li>Detecting and preventing security threats and fraudulent activity</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">
                    To Send Optional Marketing or Feature Updates
                  </h3>
                  <p className="ml-4">
                    We may send marketing emails or feature updates only where lawful (e.g., with
                    your consent or based on legitimate interest where applicable). You can
                    unsubscribe or opt out at any time using the unsubscribe link in emails or by
                    contacting us.
                  </p>
                </div>
              </div>
            </section>

            {/* 4. Legal Bases (GDPR/EEA) */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                4. Legal Bases for Processing (EEA/UK Visitors)
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  If you are located in the European Economic Area (EEA) or the United Kingdom, we
                  process your personal information based on the following legal bases:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    <strong>Contract:</strong> To process orders, deliver domains, and fulfill our
                    obligations under our Terms of Service.
                  </li>
                  <li>
                    <strong>Legitimate interests:</strong> To improve our services, prevent fraud,
                    maintain security, and analyze website usage.
                  </li>
                  <li>
                    <strong>Consent:</strong> For certain marketing emails and analytics cookies
                    where required by law.
                  </li>
                  <li>
                    <strong>Legal obligations:</strong> To comply with tax, accounting, and other
                    legal requirements.
                  </li>
                </ul>
              </div>
            </section>

            {/* 5. How We Share Information */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                5. How We Share Information
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">Service Providers</h3>
                  <p className="mb-2">
                    We share information with trusted service providers who help us operate our
                    business:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>
                      <strong>Payment processing:</strong> Stripe processes payments and handles
                      payment card data. Stripe's use of your information is governed by their
                      privacy policy.
                    </li>
                    <li>
                      <strong>Hosting and infrastructure:</strong> Vercel (hosting) and Supabase
                      (database and backend services) store and process data on our behalf.
                    </li>
                    <li>
                      <strong>Email and communication:</strong> We use Resend for transactional
                      emails (order confirmations, invoices) and Zoho for support/inbox management.
                    </li>
                    <li>
                      <strong>Analytics and error monitoring:</strong> We use PostHog (analytics)
                      and Sentry (error tracking) to improve and monitor our services. Analytics are
                      opt-in and respect user consent preferences.
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">Business Transfers</h3>
                  <p>
                    If Attain Domains is involved in a merger, acquisition, or sale of assets, your
                    information may be transferred as part of that transaction. We will notify you
                    of any such change in ownership or control.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">
                    Legal and Compliance
                  </h3>
                  <p>
                    We may share information when required by law, to protect our rights or the
                    rights of others, to respond to lawful requests from government authorities, to
                    prevent fraud, or to enforce our Terms of Service.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">
                    We Do Not Sell Personal Information
                  </h3>
                  <p>
                    We do not sell personal information in the traditional sense. We do not share
                    personal information with third parties for their own marketing purposes
                    without your consent. If any analytics or advertising practices could be
                    considered a "sale" or "sharing" under laws like the California Consumer
                    Privacy Act (CCPA), you can contact us to opt out or use available opt-out
                    mechanisms.
                  </p>
                </div>
              </div>
            </section>

            {/* 6. Cookies & Tracking Technologies */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                6. Cookies & Tracking Technologies
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  We use cookies and similar technologies to keep you logged in (if applicable),
                  remember preferences, and measure site performance via analytics.
                </p>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">Essential Cookies</h3>
                  <p>
                    These cookies are necessary for the site to function properly, such as
                    authentication and session management cookies. These cannot be disabled
                    without affecting site functionality.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">
                    Non-Essential / Analytics Cookies
                  </h3>
                </div>
                <p>
                  You can control non-essential, analytics, and all other cookies via your browser
                  settings, extensions, plugins, etc. Note that disabling cookies may affect some site
                  features. For more information, see your browser's help documentation.
                </p>
              </div>
            </section>

            {/* 7. Data Retention */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">7. Data Retention</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  We retain account and transaction information for as long as needed to provide
                  services and comply with legal and financial obligations (such as tax and
                  accounting requirements, fraud prevention, and dispute resolution).
                </p>
                <p>
                  We retain logs and analytics data for a limited period as necessary to maintain
                  and improve the service, typically up to 90 days for analytics data.
                </p>
                <p>
                  When data is no longer needed for these purposes, we delete or anonymize it,
                  subject to technical constraints and legal retention requirements.
                </p>
              </div>
            </section>

            {/* 8. Your Rights & Choices */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                8. Your Rights & Choices
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>Depending on your location, you may have the following rights:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    <strong>Access:</strong> Request a copy or explanation of what personal
                    information we hold about you.
                  </li>
                  <li>
                    <strong>Correction:</strong> Request correction of inaccurate or incomplete
                    information.
                  </li>
                  <li>
                    <strong>Deletion:</strong> Request deletion of your personal information in
                    certain circumstances (subject to legal and contractual obligations).
                  </li>
                  <li>
                    <strong>Restriction / Objection:</strong> Opt out of certain uses of your
                    information, such as marketing communications.
                  </li>
                  <li>
                    <strong>Data Portability:</strong> Request a copy of your data in a
                    machine-readable format where applicable.
                  </li>
                </ul>
                <p>
                  <strong>How to Exercise Your Rights:</strong> To exercise any of these rights,
                  please contact us via our{' '}
                  <Link to="/contact" className="text-primary hover:underline">
                    contact page
                  </Link>{' '}
                  or email us at support@attaindomains.com. We will respond to your request within
                  a reasonable timeframe.
                </p>
                <p>
                  <strong>Additional Rights:</strong> If you are located in the EEA, UK, or
                  California, local laws may provide additional rights. Please contact us for
                  details about how to exercise these rights.
                </p>
              </div>
            </section>

            {/* 9. Security */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">9. Security</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  We implement reasonable technical and organizational measures to protect your
                  personal information against unauthorized access, alteration, disclosure, or
                  destruction. These measures include:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Encryption of data in transit (HTTPS/TLS)</li>
                  <li>Secure authentication and access controls</li>
                  <li>Regular security assessments and monitoring</li>
                  <li>Secure hosting environments with industry-standard protections</li>
                </ul>
                <p>
                  However, no method of transmission over the internet or electronic storage is 100%
                  secure. While we strive to protect your information, we cannot guarantee absolute
                  security.
                </p>
              </div>
            </section>

            {/* 10. Children's Privacy */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">10. Children's Privacy</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  AttainDomains.com is not directed to children under the age of 13 (or under the
                  minimum age in your jurisdiction). We do not knowingly collect personal
                  information from children.
                </p>
                <p>
                  Accounts and purchases are intended for adults or businesses. If you are a parent
                  or guardian and believe that your child has provided us with personal information,
                  please contact us immediately so we can delete such information.
                </p>
              </div>
            </section>

            {/* 11. International Transfers */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                11. International Transfers
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  Your information may be processed and stored in the United States or other
                  countries where our service providers are located. These countries may have
                  different data protection laws than your country of residence.
                </p>
                <p>
                  When we transfer personal information from the EEA or UK to countries that may not
                  have equivalent data protection laws, we rely on appropriate safeguards, such as
                  standard contractual clauses, to protect your information.
                </p>
              </div>
            </section>

            {/* 12. Changes to This Privacy Policy */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                12. Changes to This Privacy Policy
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our
                  practices, technology, legal requirements, or other factors.
                </p>
                <p>
                  When we make changes, we will update the "Last updated" date at the top of this
                  page. Significant changes may be highlighted on our website or communicated via
                  email where appropriate.
                </p>
                <p>
                  We encourage you to review this Privacy Policy periodically to stay informed about
                  how we protect your information.
                </p>
              </div>
            </section>

            {/* 13. Contact Information */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">13. Contact Information</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  If you have questions, concerns, or requests regarding this Privacy Policy or your
                  personal information, please contact us:
                </p>
                <ul className="list-none space-y-2 ml-4">
                  <li>
                    <strong>Contact Form:</strong>{' '}
                    <Link to="/contact" className="text-primary hover:underline">
                      Visit our contact page
                    </Link>
                  </li>
                </ul>
                <p>
                  We will respond to your inquiry within a reasonable timeframe, typically within
                  30 days.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
